// OWNER: Agent C. Typed fetch wrapper. Sends the session cookie and bounces to
// /login on 401 so no screen has to think about auth.
import type { ApiResponse, ApiError } from "@shared/types";

export class ApiRequestError extends Error {
  constructor(readonly status: number, readonly apiError: ApiError) {
    super(apiError.message);
    this.name = "ApiRequestError";
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path.startsWith("/api") ? path : `/api${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (res.status === 401 && !window.location.pathname.startsWith("/login")) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`/login?next=${next}`);
    throw new ApiRequestError(401, { code: "UNAUTHENTICATED", message: "Redirecting to login" });
  }

  const body = (await res.json().catch(() => null)) as ApiResponse<T> | null;
  if (!res.ok || !body || "error" in body) {
    throw new ApiRequestError(
      res.status,
      body && "error" in body ? body.error : { code: "UNKNOWN", message: res.statusText },
    );
  }
  return body.data;
}

export const get = <T>(p: string) => api<T>(p);
export const post = <T>(p: string, body?: unknown) =>
  api<T>(p, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
export const patch = <T>(p: string, body: unknown) =>
  api<T>(p, { method: "PATCH", body: JSON.stringify(body) });
export const del = <T>(p: string) => api<T>(p, { method: "DELETE" });
